import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine,
  ResponsiveContainer, Cell,
} from "recharts";

const capitalize = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

const fmt_hours = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  return `${h.toFixed(1)} h`;
};

const fmt_days = (d) => {
  if (d == null) return "—";
  if (d < 1) return "< 1 day";
  return `${d.toFixed(1)} days`;
};

const fmt_date = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-BE", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const dayColor = (d) => {
  if (d == null) return "#888780";
  if (d < 5)  return "#3B6D11";
  if (d < 10) return "#BA7517";
  return "#A32D2D";
};

const WINDOW_OPTIONS = [
  { label: "14 days", value: 14 },
  { label: "30 days", value: 30 },
  { label: "60 days", value: 60 },
];

const CATEGORY_OPTIONS = [
  { label: "Incidents",     value: "incidents" },
  { label: "Project Tasks", value: "projects"  },
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: "var(--color-background-secondary)",
      border: "1px solid var(--color-border-tertiary)",
      borderRadius: 8, padding: "10px 14px",
      fontSize: 13, color: "var(--color-text-primary)", lineHeight: 1.8,
    }}>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>{capitalize(d.name)}</div>
      <div>Open tickets: <b>{d.open_tickets}</b></div>
      <div>Avg h/ticket: <b>{fmt_hours(d.avg_hours_per_ticket)}</b></div>
      <div>Remaining: <b>{fmt_hours(d.estimated_remaining_hours)}</b></div>
      <div>Velocity: <b>{fmt_hours(d.avg_daily_hours)}/day</b></div>
      <div>Est. clear: <b>{fmt_date(d.forecast_clear_date)}</b></div>
    </div>
  );
};

export default function Forecast() {
  const [data, setData]         = useState(null);
  const [window, setWindow]     = useState(30);
  const [category, setCategory] = useState("incidents");
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const load = async (w) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/forecast?days=${w}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(window); }, [window]);

  const members = data?.[category] ?? [];

  return (
    <div style={{ padding: "24px 32px", maxWidth: 960, margin: "0 auto" }}>

      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>Workload forecast</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
            Based on historical velocity x current open backlog
          </p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {WINDOW_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => setWindow(opt.value)} style={{
              padding: "5px 14px", borderRadius: 6,
              border: "1px solid var(--color-border-secondary)",
              background: window === opt.value ? "var(--color-background-primary)" : "transparent",
              color: window === opt.value ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              fontWeight: window === opt.value ? 500 : 400,
              fontSize: 13, cursor: "pointer",
            }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: "2px solid var(--color-border-tertiary)" }}>
        {CATEGORY_OPTIONS.map((opt) => (
          <button key={opt.value} onClick={() => setCategory(opt.value)} style={{
            padding: "8px 20px",
            border: "none",
            borderBottom: category === opt.value ? "2px solid var(--accent, #2563eb)" : "2px solid transparent",
            marginBottom: -2,
            background: "transparent",
            color: category === opt.value ? "var(--accent, #2563eb)" : "var(--color-text-secondary)",
            fontWeight: category === opt.value ? 600 : 400,
            fontSize: 14, cursor: "pointer",
          }}>
            {opt.label}
            {data && (
              <span style={{
                marginLeft: 7, fontSize: 11, fontWeight: 500,
                background: category === opt.value ? "var(--accent, #2563eb)" : "var(--color-border-secondary)",
                color: category === opt.value ? "#fff" : "var(--color-text-secondary)",
                borderRadius: 10, padding: "1px 7px",
              }}>
                {data[opt.value]?.reduce((s, m) => s + m.open_tickets, 0) ?? "..."}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: "var(--color-text-secondary)", fontSize: 14 }}>Loading...</p>}
      {error   && <p style={{ color: "var(--color-text-danger)", fontSize: 14 }}>Error: {error}</p>}

      {!loading && !error && (
        <>
          {/* Bar chart */}
          <div style={{
            background: "var(--color-background-secondary)",
            border: "1px solid var(--color-border-tertiary)",
            borderRadius: 12, padding: "20px 24px 12px", marginBottom: 24,
          }}>
            <p style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary)" }}>
              Estimated working days to clear open backlog
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={members} margin={{ top: 4, right: 16, left: -12, bottom: 0 }}>
                <XAxis dataKey="name" tickFormatter={capitalize}
                  tick={{ fontSize: 12, fill: "var(--color-text-secondary)" }}
                  axisLine={false} tickLine={false} />
                <YAxis unit=" d"
                  tick={{ fontSize: 12, fill: "var(--color-text-secondary)" }}
                  axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ opacity: 0.08 }} />
                <ReferenceLine y={5}  stroke="#3B6D11" strokeDasharray="4 3" strokeWidth={1}
                  label={{ value: "5 d",  position: "right", fontSize: 11, fill: "#3B6D11" }} />
                <ReferenceLine y={10} stroke="#BA7517" strokeDasharray="4 3" strokeWidth={1}
                  label={{ value: "10 d", position: "right", fontSize: 11, fill: "#BA7517" }} />
                <Bar dataKey="estimated_days" radius={[4, 4, 0, 0]} maxBarSize={52}>
                  {members.map((entry, i) => (
                    <Cell key={i} fill={dayColor(entry.estimated_days)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Per-member cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
            {members.map((m) => {
              const color = dayColor(m.estimated_days);
              return (
                <div key={m.name} style={{
                  background: "var(--color-background-secondary)",
                  border: "1px solid var(--color-border-tertiary)",
                  borderLeft: `4px solid ${color}`,
                  borderRadius: 10, padding: "14px 18px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <span style={{ fontWeight: 500, fontSize: 15 }}>{capitalize(m.name)}</span>
                    <span style={{
                      fontSize: 12, fontWeight: 500, color,
                      background: color + "18", border: `1px solid ${color}40`,
                      borderRadius: 6, padding: "2px 8px",
                    }}>
                      {fmt_days(m.estimated_days)}
                    </span>
                  </div>

                  <div style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr",
                    gap: "6px 0", fontSize: 13, color: "var(--color-text-secondary)",
                  }}>
                    <span>Open tickets</span>
                    <span style={{ color: "var(--color-text-primary)", fontWeight: 500, textAlign: "right" }}>{m.open_tickets}</span>
                    <span>Velocity</span>
                    <span style={{ color: "var(--color-text-primary)", fontWeight: 500, textAlign: "right" }}>{fmt_hours(m.avg_daily_hours)}/day</span>
                    <span>Avg per ticket</span>
                    <span style={{ color: "var(--color-text-primary)", fontWeight: 500, textAlign: "right" }}>{fmt_hours(m.avg_hours_per_ticket)}</span>
                    <span>Total remaining</span>
                    <span style={{ color: "var(--color-text-primary)", fontWeight: 500, textAlign: "right" }}>{fmt_hours(m.estimated_remaining_hours)}</span>
                    <span>Active days ({m.velocity_window_days}d)</span>
                    <span style={{ color: "var(--color-text-primary)", fontWeight: 500, textAlign: "right" }}>{m.days_active}</span>
                  </div>

                  <div style={{
                    marginTop: 12, paddingTop: 10,
                    borderTop: "1px solid var(--color-border-tertiary)",
                    fontSize: 12, color: "var(--color-text-secondary)",
                    display: "flex", justifyContent: "space-between",
                  }}>
                    <span>Forecast clear date</span>
                    <span style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>
                      {m.has_velocity ? fmt_date(m.forecast_clear_date) : "No data"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <p style={{ marginTop: 20, fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
            Velocity = avg hours logged per active day over the last {window} days.
            Remaining = open {category === "incidents" ? "incident" : "project"} ticket count x avg time per resolved ticket (member average; falls back to category average when fewer than 3 resolved tickets).
            Forecast date skips weekends.
          </p>
        </>
      )}
    </div>
  );
}
